import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ChatConversation } from 'lib-trend-core';
import {
  ChatActions,
  bootstrapFailedSelector,
  bootstrapLoadedSelector,
  buildChatAppPath,
  conversationsSelector,
} from 'lib-trend-core/chat';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { ChatAppActivationService } from '../core/chat-app-activation.service';

/** What the route can show for the addressed conversation. */
type ChatAppConversationState = 'loading' | 'ready' | 'notAvailable' | 'failed';

/**
 * `/c/:channelId`, `/c/:channelId/m/:messageId`, `/c/:channelId/t/:threadRootId` —
 * SPEC-2026-09-internal-chat-pwa (T-31 · RF-02).
 *
 * The address is applied to the store (the conversation becomes the active one), the
 * message is revealed or the thread opened, and a reload lands in exactly the same state.
 * An id the user cannot see — not a member of a private channel, deleted, never existed —
 * renders the same "not available" state, which names nothing: the server's own
 * membership rules decide what is in the user's conversation list.
 */
@Component({
  selector: 'chat-app-conversation-route',
  template: `
    @switch (state) {
      @case ('ready') {
        <chat-conversation
          class="d-block flex-grow-1 chat-app__conversation"
          [channelId]="channelId"
          [focusMessageId]="messageId"
          [threadRootId]="threadRootId"
          [showBack]="true"
          (back)="backToList()"
          (threadOpenChange)="onThreadChange($event)"
        ></chat-conversation>
      }
      @case ('notAvailable') {
        <chat-app-pane-header titleKey="chatApp.conversation.notAvailableTitle"></chat-app-pane-header>
        <chat-empty-state
          class="flex-grow-1"
          icon="ri-lock-line"
          titleKey="chatApp.conversation.notAvailableTitle"
          hintKey="chatApp.conversation.notAvailableHint"
        ></chat-empty-state>
      }
      @case ('failed') {
        <chat-error-state class="flex-grow-1" (retry)="retry()"></chat-error-state>
      }
      @default {
        <chat-message-list-skeleton class="flex-grow-1"></chat-message-list-skeleton>
      }
    }
  `,
  host: { class: 'd-flex flex-column flex-grow-1 overflow-hidden' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAppConversationRouteComponent implements OnInit, OnDestroy {

  channelId: string | null = null;
  messageId: string | null = null;
  threadRootId: string | null = null;

  state: ChatAppConversationState = 'loading';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly activation: ChatAppActivationService,
  ) { }

  ngOnInit(): void {
    const params$ = this.route.paramMap.pipe(
      map((params) => ({
        channelId: params.get('channelId'),
        messageId: params.get('messageId'),
        threadRootId: params.get('threadRootId'),
      })),
    );

    params$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ channelId, messageId, threadRootId }) => {
        if (channelId !== this.channelId) {
          this.state = 'loading';
        }
        this.channelId = channelId;
        this.messageId = messageId;
        this.threadRootId = threadRootId;
        if (channelId) {
          this.store.dispatch(ChatActions.openConversation({ channelId }));
        }
      });

    combineLatest([
      params$.pipe(map((params) => params.channelId), distinctUntilChanged()),
      this.store.select(bootstrapLoadedSelector),
      this.store.select(bootstrapFailedSelector),
      this.store.select(conversationsSelector),
    ])
      .pipe(
        filter(([, loaded, failed]) => loaded || failed),
        takeUntil(this.destroy$),
      )
      .subscribe(([channelId, , failed, conversations]) => this.settle(channelId, failed, conversations));
  }

  private settle(channelId: string | null, failed: boolean, conversations: ChatConversation[]): void {
    if (!channelId) return;
    if (failed && this.state !== 'ready') {
      this.state = 'failed';
      return;
    }
    const exists = (conversations ?? []).some((conversation) => conversation._id === channelId);
    if (exists) {
      if (this.state !== 'ready') {
        this.state = 'ready';
        this.activation.resolve(channelId, true);
      }
      return;
    }
    // A conversation that was on screen and left the list (deleted, left elsewhere).
    this.state = 'notAvailable';
    this.activation.resolve(channelId, false);
  }

  retry(): void {
    this.state = 'loading';
    this.store.dispatch(ChatActions.loadBootstrap());
  }

  backToList(): void {
    this.router.navigateByUrl('/');
  }

  /** The thread opened or closed from inside the pane: the address follows. */
  onThreadChange(threadRootId: string | null): void {
    if (!this.channelId || (threadRootId ?? null) === (this.threadRootId ?? null)) return;
    this.router.navigateByUrl(buildChatAppPath({ channelId: this.channelId, threadRootId: threadRootId ?? undefined }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
